import { authedFetch } from '../lib/authedFetch';
import {
  analysisRevisionReferenceV1Schema,
  decisionAnalysisDomainSchema,
  decisionAnalysisSnapshotV1Schema,
  decisionAnalysisInputEnvelopeV1Schema,
  type AnalysisRevisionReferenceV1,
  type DecisionAnalysisDomain,
  type DecisionAnalysisSnapshotV1,
  type DecisionAnalysisInputEnvelopeV1,
} from '../types/decisionAnalysis';

const ROUTE_BASE = '/api/decision-analyses';

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

async function responseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: text };
  }
}

function errorMessage(value: unknown, fallback: string): string {
  const body = record(value);
  return typeof body?.error === 'string' && body.error.trim()
    ? body.error.trim()
    : fallback;
}

export interface CurrentDecisionAnalysis {
  domain: DecisionAnalysisDomain;
  reference?: AnalysisRevisionReferenceV1;
  latestAttemptReference?: AnalysisRevisionReferenceV1;
  refreshState?:
    | 'queued'
    | 'refreshing'
    | 'ready'
    | 'stale'
    | 'held'
    | 'degraded'
    | 'failed';
  refreshInputHash?: string;
  refreshJobId?: string;
  refreshRequestedAt?: string;
  refreshStartedAt?: string;
  refreshUpdatedAt?: string;
  refreshCompletedAt?: string;
  refreshFailedAt?: string;
  refreshReason?: string;
  refreshError?: string;
  refreshProgress?: number;
  refreshStage?: string;
  /**
   * Set while the consecutive-rebind cap is holding this domain: nothing will
   * re-run it automatically. Cleared by any commit that reads current and by an
   * operator's own rerun (finalizeReportRevision AUTO_REBIND_MAX_CONSECUTIVE).
   */
  autoRebindExhaustedAt?: string;
  /** Consecutive commits that read input-stale; absent once one reads current. */
  autoRebindStreak?: number;
  /**
   * Set when the latest stale commit armed NO automatic rerun because only
   * optional currency evidence (public sentiment, economic evidence) moved:
   * newer evidence is available and the operator reruns when they want it.
   */
  autoRebindSkippedAt?: string;
  /** The optional sections behind `autoRebindSkippedAt`. */
  autoRebindSkippedSections?: string[];
  reportId?: string;
  reportRevisionId?: string;
  updatedAt?: string;
}

export interface CurrentDecisionAnalysesResponse {
  /**
   * Server-owned rollout decision. The browser must never infer this from its
   * Vite build flags because a shadow/read UI may be served by a non-gating
   * API deployment.
   */
  analysisGateEnabled: boolean;
  /** Missing envelopes withhold currentness; never substitute saved inputs. */
  currentAnalysisInputsByDomain?: Partial<Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>>;
  analyses: CurrentDecisionAnalysis[];
}

export async function loadCurrentDecisionAnalyses(
  siteId: string,
  options: {
    ownerUid?: string;
    signal?: AbortSignal;
    fetcher?: typeof authedFetch;
  } = {},
): Promise<CurrentDecisionAnalysesResponse> {
  const query = options.ownerUid
    ? `?ownerUid=${encodeURIComponent(options.ownerUid)}`
    : '';
  const response = await (options.fetcher ?? authedFetch)(
    `${ROUTE_BASE}/${encodeURIComponent(siteId)}${query}`,
    { signal: options.signal },
  );
  const body = await responseJson(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Could not load decision analyses (${response.status}).`));
  }
  const payload = record(body);
  if (
    !payload
    || !Array.isArray(payload.analyses)
    || typeof payload.analysisGateEnabled !== 'boolean'
  ) {
    throw new Error('The decision analysis service returned an invalid current-pointer list.');
  }
  const analyses = payload.analyses.map((value) => {
    const raw = record(value);
    if (!raw) throw new Error('The decision analysis service returned an invalid pointer.');
    const domain = decisionAnalysisDomainSchema.safeParse(raw.domain);
    const parsed = analysisRevisionReferenceV1Schema.safeParse({
      schemaVersion: raw.schemaVersion,
      analysisId: raw.analysisId,
      siteId: raw.siteId,
      domain: raw.domain,
      inputHash: raw.inputHash,
      contentHash: raw.contentHash,
      snapshotPath: raw.snapshotPath,
      createdAt: raw.createdAt,
      readiness: raw.readiness,
    });
    const latestAttemptReference = analysisRevisionReferenceV1Schema.safeParse(
      raw.latestAttemptReference,
    );
    const refreshState = (
      typeof raw.refreshState === 'string'
      && [
        'queued',
        'refreshing',
        'ready',
        'stale',
        'held',
        'degraded',
        'failed',
      ].includes(raw.refreshState)
    )
      ? raw.refreshState as CurrentDecisionAnalysis['refreshState']
      : undefined;
    if (
      !domain.success
      || (
        !parsed.success
        && !latestAttemptReference.success
        && refreshState == null
      )
      || (parsed.success && (
        parsed.data.domain !== domain.data
        || parsed.data.siteId !== siteId
      ))
      || (latestAttemptReference.success && (
        latestAttemptReference.data.domain !== domain.data
        || latestAttemptReference.data.siteId !== siteId
      ))
    ) {
      throw new Error('The decision analysis service returned an invalid pointer.');
    }
    return {
      domain: domain.data,
      ...(parsed.success ? { reference: parsed.data } : {}),
      ...(latestAttemptReference.success
        ? { latestAttemptReference: latestAttemptReference.data }
        : {}),
      ...(refreshState ? { refreshState } : {}),
      ...(typeof raw.refreshInputHash === 'string'
        && /^[a-f0-9]{64}$/.test(raw.refreshInputHash)
        ? { refreshInputHash: raw.refreshInputHash }
        : {}),
      ...(typeof raw.refreshJobId === 'string'
        ? { refreshJobId: raw.refreshJobId }
        : {}),
      ...(Array.isArray(raw.autoRebindSkippedSections)
        && raw.autoRebindSkippedSections.every((section: unknown) => typeof section === 'string')
        ? { autoRebindSkippedSections: [...raw.autoRebindSkippedSections as string[]] }
        : {}),
      ...(typeof raw.autoRebindStreak === 'number' && Number.isFinite(raw.autoRebindStreak)
        ? { autoRebindStreak: raw.autoRebindStreak }
        : {}),
      ...(typeof raw.refreshProgress === 'number' && Number.isFinite(raw.refreshProgress)
        ? { refreshProgress: raw.refreshProgress }
        : {}),
      ...([
        'refreshRequestedAt',
        'refreshStartedAt',
        'refreshUpdatedAt',
        'refreshCompletedAt',
        'refreshFailedAt',
        'refreshReason',
        'refreshError',
        'refreshStage',
        'autoRebindExhaustedAt',
        'autoRebindSkippedAt',
      ] as const).reduce<Record<string, string>>((result, field) => {
        if (typeof raw[field] === 'string') result[field] = raw[field];
        return result;
      }, {}),
      ...(typeof raw.reportId === 'string' ? { reportId: raw.reportId } : {}),
      ...(typeof raw.reportRevisionId === 'string'
        ? { reportRevisionId: raw.reportRevisionId }
        : {}),
      ...(typeof raw.updatedAt === 'string' ? { updatedAt: raw.updatedAt } : {}),
    };
  });
  const currentAnalysisInputsByDomain: Partial<Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>> = {};
  for (const [key, value] of Object.entries(record(payload.currentAnalysisInputsByDomain) ?? {})) {
    const domain = decisionAnalysisDomainSchema.safeParse(key);
    const input = decisionAnalysisInputEnvelopeV1Schema.safeParse(value);
    if (!domain.success || !input.success || input.data.siteId !== siteId || input.data.domain !== domain.data) {
      throw new Error('The decision analysis service returned an invalid current source envelope.');
    }
    currentAnalysisInputsByDomain[domain.data] = input.data;
  }
  return {
    analysisGateEnabled: payload.analysisGateEnabled,
    currentAnalysisInputsByDomain,
    analyses,
  };
}

export async function loadDecisionAnalysisRevision(
  siteId: string,
  analysisId: string,
  options: {
    ownerUid?: string;
    signal?: AbortSignal;
    fetcher?: typeof authedFetch;
  } = {},
): Promise<{
  reference: AnalysisRevisionReferenceV1;
  analysis: DecisionAnalysisSnapshotV1;
}> {
  const query = options.ownerUid
    ? `?ownerUid=${encodeURIComponent(options.ownerUid)}`
    : '';
  const response = await (options.fetcher ?? authedFetch)(
    `${ROUTE_BASE}/${encodeURIComponent(siteId)}/revisions/${encodeURIComponent(analysisId)}${query}`,
    { signal: options.signal },
  );
  const body = await responseJson(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Could not load analysis revision (${response.status}).`));
  }
  const payload = record(body);
  const reference = analysisRevisionReferenceV1Schema.safeParse(payload?.reference);
  const analysis = decisionAnalysisSnapshotV1Schema.safeParse(payload?.analysis);
  if (
    !reference.success
    || !analysis.success
    || reference.data.analysisId !== analysis.data.analysisId
    || reference.data.inputHash !== analysis.data.inputHash
    || reference.data.contentHash !== analysis.data.contentHash
  ) {
    throw new Error('The decision analysis service returned a mismatched immutable revision.');
  }
  return { reference: reference.data, analysis: analysis.data };
}

export async function retryDecisionAnalysis(
  siteId: string,
  domain: DecisionAnalysisDomain,
  options: {
    ownerUid?: string;
    idempotencyKey?: string;
    signal?: AbortSignal;
    fetcher?: typeof authedFetch;
  } = {},
): Promise<{
  jobId: string;
  clientRunId: string;
  status: string;
  duplicate: boolean;
}> {
  const parsedDomain = decisionAnalysisDomainSchema.parse(domain);
  const idempotencyKey = options.idempotencyKey
    ?? globalThis.crypto?.randomUUID?.()
    ?? `retry-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const response = await (options.fetcher ?? authedFetch)(
    `${ROUTE_BASE}/${encodeURIComponent(siteId)}/${encodeURIComponent(parsedDomain)}/retry`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idempotencyKey,
        ...(options.ownerUid ? { ownerUid: options.ownerUid } : {}),
      }),
      signal: options.signal,
    },
  );
  const body = await responseJson(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Could not retry analysis (${response.status}).`));
  }
  const payload = record(body);
  if (
    !payload
    || typeof payload.jobId !== 'string'
    || typeof payload.clientRunId !== 'string'
    || typeof payload.status !== 'string'
  ) {
    throw new Error('The decision analysis service returned an invalid retry receipt.');
  }
  return {
    jobId: payload.jobId,
    clientRunId: payload.clientRunId,
    status: payload.status,
    duplicate: payload.duplicate === true,
  };
}
